import { useState } from "react";
import { Bell, AlertTriangle, CheckCircle2, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetUpcomingLandscapeSchedules } from "@/resources/landscape-schedule";
import { notificationApi } from "@/resources/notification";
import type { Notification } from "@/resources/notification/notification.api";
import { NotificationsModal } from "@/components/layout/NotificationsModal";
import { TruncatedText } from "@/components/ui/truncated-text";
import { format, parseISO } from "date-fns";
import { ManageSchedulePopover } from "@/components/dashboard/ManageSchedulePopover";
import { useAppData } from "@/contexts/AppDataContext";

type NotifIconConfig = { icon: React.ElementType; className: string };

const NOTIFICATION_TYPE_CONFIG: Record<string, NotifIconConfig> = {
  signal: { icon: CheckCircle2, className: "text-green-600" },
  landscape_complete: { icon: CheckCircle2, className: "text-green-600" },
  rank_up: { icon: CheckCircle2, className: "text-green-600" },
  rank_down: { icon: AlertTriangle, className: "text-destructive" },
  ab_test_results: { icon: Bell, className: "text-muted-foreground" },
};

const DEFAULT_NOTIF_CONFIG: NotifIconConfig = { icon: Bell, className: "text-muted-foreground" };

function getNotificationType(n: Notification): string {
  return n.data?.type ?? n.type ?? "default";
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatFrequency(scheduleType: "one_time" | "recurring", frequency: string | null): string {
  if (scheduleType === "one_time") return "One-time";
  if (!frequency) return "Recurring";
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
}

export function DashboardActivityPanel() {
  const [modalOpen, setModalOpen] = useState(false);
  const { selectedBrandId } = useAppData();

  const { data: upcomingData, isLoading: upcomingLoading } = useGetUpcomingLandscapeSchedules(5);
  const upcomingItems = upcomingData?.items ?? [];

  const { data: notificationsData, isLoading: notificationsLoading } =
    notificationApi.useGetNotifications(
      selectedBrandId
        ? { per_page: 5, page: 1, brand_id: selectedBrandId }
        : { per_page: 5, page: 1 }
    );
  const notifications = notificationsData?.data ?? [];



  return (
    <div className="space-y-4 w-full">
      {/* Upcoming Refreshes */}
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Upcoming Refreshes</CardTitle>
            <span className="text-xs text-muted-foreground">{upcomingItems.length} scheduled</span>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-1">
            {upcomingLoading ? (
              <>
                {Array.from({ length: 3 })?.map((_, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-0">
                    <Skeleton className="h-6 w-6 rounded mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-2.5 w-1/2" />
                    </div>
                  </div>
                ))}
              </>
            ) : upcomingItems.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No upcoming refreshes scheduled</p>
            ) : (
              upcomingItems?.map((item) => (
                <div key={item.id} className="flex items-center gap-2.5 py-2.5 border-b border-border last:border-0">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-sm font-medium text-foreground min-w-0">
                        <TruncatedText>{item.landscapePhrase}</TruncatedText>
                      </p>
                      <ManageSchedulePopover schedule={item} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.nextRunAt ? format(parseISO(item.nextRunAt), "MMM d, yyyy") : "TBD"} ·{" "}
                      {formatFrequency(item.scheduleType, item.frequency)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setModalOpen(true)}
            >
              View all
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-1">
            {notificationsLoading ? (
              <>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-2 border-b border-border last:border-0">
                    <Skeleton className="h-4 w-4 rounded-full mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-2.5 w-1/3" />
                    </div>
                  </div>
                ))}
              </>
            ) : notifications.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No recent activity</p>
            ) : (
              notifications?.map((n) => {
                const type = getNotificationType(n);
                const { icon: Icon, className: iconClass } = NOTIFICATION_TYPE_CONFIG[type] ?? DEFAULT_NOTIF_CONFIG;
                const message = n.data?.body
                  ? `${n.data.title} — ${n.data.body}`
                  : n.data?.title;
                return (
                  <div
                    key={n.id}
                    className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-0"
                  >
                    <div className={`mt-1 shrink-0 ${iconClass}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-relaxed">{message}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatRelativeTime(n.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      <NotificationsModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
